/**
 * ResultRow.tsx — Phase-2 calculation result row with count-up animation.
 *
 * Shows a pending shimmer (bouncing dots) while `resolved` is false, then
 * animates the numeric value from 0 to `numericValue` via useCountUp once
 * the backend data arrives.
 */

import React from 'react';
import { useCountUp } from './useCountUp';

/** Formato número con separador de miles ES (1.230 en vez de 1230).
 * useGrouping: true es necesario explícito — en Chromium con locale 'es-ES'
 * el default no agrupa (vi "1870" en vez de "1.870" durante el QA visual). */
export const fmt = (n: number): string => n.toLocaleString('es-ES', { useGrouping: true });

export interface ResultRowProps {
  stageKey: string;
  icon: string;
  /** Label suffix (e.g. "recetas idóneas") prepended by the number. */
  label: string;
  visible: boolean;
  resolved: boolean;
  /** Numeric value for count-up animation. */
  numericValue?: number;
  /** Suffix appended to the number (e.g. "kcal diarias"). */
  unitSuffix?: string;
}

export const ResultRow: React.FC<ResultRowProps> = ({
  stageKey,
  icon,
  label,
  visible,
  resolved,
  numericValue,
  unitSuffix = '',
}) => {
  const countedValue = useCountUp(numericValue, resolved && visible);

  return (
    <div
      className={`flex items-center gap-3 transition-all duration-400 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
      aria-hidden={!visible}
    >
      <span
        className={`text-lg flex-shrink-0 transition-transform duration-300 ${visible ? 'scale-100' : 'scale-50'}`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.34,1.56,0.64,1)' }}
      >
        {icon}
      </span>

      <span className="flex-1 text-sm font-medium">
        {resolved ? (
          <>
            <span className="text-base font-bold text-[#008BBC] tabular-nums">
              {fmt(countedValue)}
              {unitSuffix}
            </span>{' '}
            <span className="text-gray-700">{label}</span>
          </>
        ) : (
          <span className="text-gray-500">
            <span className="inline-flex gap-0.5 mr-1 align-middle">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-1 h-1 bg-gray-300 rounded-full animate-bounce inline-block"
                  style={{ animationDelay: `${i * 150}ms` }}
                />
              ))}
            </span>
            {label}
          </span>
        )}
      </span>

      {resolved && <span className="text-green-500 font-bold text-sm flex-shrink-0">✓</span>}
    </div>
  );
};
